import type { StickerPlacement } from '@renderer/lib/stickers/bakeStickers'
import type { BrushStroke } from '@renderer/lib/stickers/brush'

/** What one Undo step restores. */
interface Snapshot {
  placements: StickerPlacement[]
  strokes: BrushStroke[]
  manual: boolean
}

/** The stickers and drawing on the strip, and the steps Undo and Redo move through. */
export interface PlacementState extends Snapshot {
  history: Snapshot[]
  /** Steps Undo took back, nearest first; any new change clears them. */
  future: Snapshot[]
}

export type PlacementAction =
  /** Auto layout: every sticker replaced by a fresh layout. Drawing stays. */
  | { type: 'replace'; placements: StickerPlacement[] }
  /** One more sticker, placed by hand. */
  | { type: 'add'; placement: StickerPlacement }
  | { type: 'remove'; id: string }
  | { type: 'move'; id: string; xPct: number; yPct: number }
  | { type: 'transform'; id: string; widthPct: number; rotation: number }
  /** Save the current strip as an undo step, before a drag or pinch changes it. */
  | { type: 'checkpoint' }
  /** A finished brush stroke. */
  | { type: 'stroke'; stroke: BrushStroke }
  /** Take everything off: stickers and drawing. */
  | { type: 'clear' }
  | { type: 'undo' }
  | { type: 'redo' }

export const initialPlacements: PlacementState = {
  placements: [],
  strokes: [],
  manual: false,
  history: [],
  future: []
}

/** Undo goes this far back; older steps are dropped. */
const HISTORY_LIMIT = 30

/** A sticker never shrinks to nothing or grows past the strip it sits on. */
export const MIN_WIDTH_PCT = 6
export const MAX_WIDTH_PCT = 80

const snapshot = (state: PlacementState): Snapshot => ({
  placements: state.placements,
  strokes: state.strokes,
  manual: state.manual
})

const withStep = (state: PlacementState): Snapshot[] =>
  [...state.history, snapshot(state)].slice(-HISTORY_LIMIT)

/**
 * `manual` is whether the guest has placed, moved, resized, turned or removed
 * a sticker by hand since the last auto layout. A new auto layout wipes the
 * stickers, so the page asks first only when that would throw away their own
 * work — re-rolling an untouched layout never needs a question.
 */
export function placementsReducer(state: PlacementState, action: PlacementAction): PlacementState {
  switch (action.type) {
    case 'replace':
      return {
        ...state,
        placements: action.placements,
        manual: false,
        history: withStep(state),
        future: []
      }
    case 'add':
      return {
        ...state,
        placements: [...state.placements, action.placement],
        manual: true,
        history: withStep(state),
        future: []
      }
    case 'remove':
      return {
        ...state,
        placements: state.placements.filter((p) => p.id !== action.id),
        manual: true,
        history: withStep(state),
        future: []
      }
    case 'move':
      return {
        ...state,
        placements: state.placements.map((p) =>
          p.id === action.id ? { ...p, xPct: action.xPct, yPct: action.yPct } : p
        )
      }
    case 'transform':
      return {
        ...state,
        placements: state.placements.map((p) =>
          p.id === action.id
            ? {
                ...p,
                widthPct: Math.min(MAX_WIDTH_PCT, Math.max(MIN_WIDTH_PCT, action.widthPct)),
                rotation: action.rotation
              }
            : p
        )
      }
    case 'checkpoint':
      // Only a drag or pinch asks for a checkpoint, so it is a hand edit.
      return { ...state, manual: true, history: withStep(state), future: [] }
    case 'stroke':
      return {
        ...state,
        strokes: [...state.strokes, action.stroke],
        history: withStep(state),
        future: []
      }
    case 'clear':
      if (state.placements.length === 0 && state.strokes.length === 0) return state
      return { placements: [], strokes: [], manual: false, history: withStep(state), future: [] }
    case 'undo': {
      if (state.history.length === 0) return state
      const last = state.history[state.history.length - 1]
      return {
        ...last,
        history: state.history.slice(0, -1),
        future: [snapshot(state), ...state.future].slice(0, HISTORY_LIMIT)
      }
    }
    case 'redo': {
      if (state.future.length === 0) return state
      const [next, ...rest] = state.future
      return { ...next, history: withStep(state), future: rest }
    }
  }
}
